import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Card, TitlePlaceholderLoader, ListItemPlaceholderLoader } from "@similarweb/ui-components";
import { Flex, Text, Box } from "@similarweb/ui-components/foundations";

import GAVerifiedContainer from "app/components/React/GAVerifiedIcon/GAVerifiedContainer";
import { abbrNumberFilter, minAbbrNumberFilter } from "app/filters/ngFilters";
import type { IWebsiteAnalysisTrafficRequestParams } from "app/pages/website-analysis/TrafficAndEngagement/types";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";

import type { IMonthlyActiveUsersResponse } from "./useMonthlyActiveUsersData";
import { useMonthlyActiveUsersData } from "./useMonthlyActiveUsersData";

const MAUDataRow = ({
    labelKey,
    dataKey,
    data,
    siteKey,
    showGAApprovedData,
    isGAPrivate,
}: {
    labelKey: string;
    dataKey: "AverageTotalVisitsMonthly" | "AverageUniqueVisitorsMonthly" | "VisitsPerUser";
    data: IMonthlyActiveUsersResponse | undefined;
    siteKey: string;
    showGAApprovedData: boolean;
    isGAPrivate: boolean;
}) => {
    const { translate } = useTranslate();
    const value = data?.Data?.[siteKey]?.[dataKey];
    const filteredNumber = dataKey === "VisitsPerUser" ? abbrNumberFilter()(value) : minAbbrNumberFilter()(value);

    return (
        <Flex
            justifyContent="space-between"
            alignItems="center"
            padding="8px 0"
        >
            <Text
                fontVariant="body1"
                color={colorsPalettes.carbon[400]}
            >
                {translate(labelKey)}
            </Text>
            <Flex
                alignItems="center"
                gap="4px"
            >
                <Text
                    fontVariant="h6"
                    color={colorsPalettes.carbon[500]}
                >
                    {filteredNumber}
                </Text>
                {showGAApprovedData ? (
                    <GAVerifiedContainer
                        size="SMALL"
                        isActive={true}
                        isPrivate={isGAPrivate}
                        tooltipAvailable={true}
                        metric={dataKey}
                        tooltipIsActive={false}
                        top={4}
                    />
                ) : null}
            </Flex>
        </Flex>
    );
};

interface IMonthlyActiveUsersWidgetProps {
    params: {
        country: number;
        from: string;
        to: string;
        key: string;
        webSource: string;
        isWindow: boolean;
        isWWW: boolean;
    };
    showGAApprovedData: boolean;
    isGAPrivate: boolean;
}

export const MonthlyActiveUsersWidget = ({
    params,
    showGAApprovedData,
    isGAPrivate,
}: IMonthlyActiveUsersWidgetProps) => {
    const apiParams: IWebsiteAnalysisTrafficRequestParams = useMemo(
        () => ({
            country: params.country.toString(),
            from: params.from,
            to: params.to,
            keys: params.key,
            webSource: params.webSource,
            isWindow: params.isWindow,
            includeSubDomains: !params.isWWW,
            timeGranularity: "Monthly",
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ShouldGetVerifiedData: showGAApprovedData,
        }),
        [
            params.country,
            params.from,
            params.to,
            params.key,
            params.webSource,
            params.isWindow,
            params.isWWW,
            showGAApprovedData,
        ],
    );

    const { data, isLoading, isError } = useMonthlyActiveUsersData(apiParams);

    const isEmpty = (data?.Data?.[params.key]?.Trend?.length ?? 0) === 0;

    if (isLoading) {
        return (
            <Card data-automation="monthly-active-users-widget">
                <Flex
                    flexDirection="column"
                    padding="16px 24px"
                    gap="16px"
                >
                    <TitlePlaceholderLoader />
                    <ListItemPlaceholderLoader />
                    <ListItemPlaceholderLoader />
                </Flex>
            </Card>
        );
    }

    return (
        <Card data-automation="monthly-active-users-widget">
            {isError || isEmpty ? (
                <Flex
                    justifyContent="center"
                    alignItems="center"
                    flex="1"
                    height="100%"
                >
                    <NoDataLandscape
                        title="global.nodata.notavilable"
                        subtitle={null}
                    />
                </Flex>
            ) : (
                <Flex
                    flexDirection="column"
                    padding="16px 24px"
                >
                    <MAUDataRow
                        labelKey="wa.ao.graph.avgvisits"
                        dataKey="AverageTotalVisitsMonthly"
                        data={data}
                        siteKey={params.key}
                        showGAApprovedData={showGAApprovedData}
                        isGAPrivate={isGAPrivate}
                    />
                    <Box
                        width="100%"
                        height="1px"
                        background={colorsPalettes.carbon[100]}
                    />
                    <MAUDataRow
                        labelKey="metric.monthlyuniquevisitors"
                        dataKey="AverageUniqueVisitorsMonthly"
                        data={data}
                        siteKey={params.key}
                        showGAApprovedData={showGAApprovedData}
                        isGAPrivate={isGAPrivate}
                    />
                    <Box
                        width="100%"
                        height="1px"
                        background={colorsPalettes.carbon[100]}
                    />
                    <MAUDataRow
                        labelKey="metric.visitsperuser"
                        dataKey="VisitsPerUser"
                        data={data}
                        siteKey={params.key}
                        showGAApprovedData={showGAApprovedData}
                        isGAPrivate={isGAPrivate}
                    />
                </Flex>
            )}
        </Card>
    );
};
